// components/Courses/AddOrEditCoursePage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Row, Col, Spinner, Modal } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Save, RotateCcw, BookOpen, IndianRupee, Image, Clock,
  Layers, Info, Users, Link2, Eye, Shield,
} from "lucide-react";
import { BackButton, AppButton } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import { usePlan } from "../../contexts/PlanContext";
import "../SelfPaced/_self-paced-pages.scss";
import "./Courses.scss";

const initialState = {
  name: "",
  shortCode: "",
  title: "",
  description: "",
  fees: "",
  freeDemo: "Yes",
  duration: "",
  mode: "Classroom",
  syllabus: "",
  prerequisites: "",
  highlights: "",
  maxStrength: "",
  enrollmentLink: "",
  allowSelfEnrollment: false,
  videoSecurity: false,
  thumbnail: null,
  banner: null,
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "list", "indent", "color", "background", "link",
];

/* ── Section wrapper with icon ───────────────────────────── */
const Section = ({ icon, title, subtitle, children, className = "" }) => (
  <div className={`sp-form-section ${className}`}>
    <div className="sp-form-section-header">
      <div className="sp-form-section-icon">{icon}</div>
      <div>
        <h6 className="sp-form-section-title">{title}</h6>
        {subtitle && <p className="sp-form-section-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="sp-form-section-body">{children}</div>
  </div>
);

/* ── Field wrapper ───────────────────────────────────────── */
const Field = ({ label, required, hint, children }) => (
  <div className="sp-field">
    <label className="sp-field-label">
      {label} {required && <span style={{ color: "#e11d48" }}>*</span>}
    </label>
    {children}
    {hint && <span className="sp-field-hint">{hint}</span>}
  </div>
);

const AddOrEditCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(!!courseId);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [previewImg, setPreviewImg] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [bannerDragActive, setBannerDragActive] = useState(false);
  const [previewModal, setPreviewModal] = useState({ show: false, title: "", html: "" });
  const fileRef = useRef(null);
  const bannerRef = useRef(null);

  // Fetch course for edit
  useEffect(() => {
    if (courseId) {
      (async () => {
        try {
          const { data } = await apiClient.get(`/courses/${courseId}`);
          const c = data.course || data;
          setForm({
            ...initialState,
            name: c.name || "",
            shortCode: c.shortCode || "",
            title: c.title || "",
            description: c.description || "",
            fees: c.fees ?? "",
            freeDemo: c.freeDemo || "Yes",
            duration: c.duration || "",
            mode: c.mode || "Classroom",
            syllabus: c.syllabus || "",
            prerequisites: c.prerequisites || "",
            highlights: c.highlights || "",
            maxStrength: c.maxStrength ?? "",
            enrollmentLink: c.enrollmentLink || "",
            allowSelfEnrollment: c.allowSelfEnrollment || false,
            videoSecurity: c.videoSecurity || false,
            thumbnail: null,
            banner: null,
          });
          if (c.thumbnailUrl || c.thumbnail) setPreviewImg(c.thumbnailUrl || c.thumbnail);
          if (c.bannerUrl || c.banner) setBannerPreview(c.bannerUrl || c.banner);
        } catch {
          setToast({ show: true, message: "Failed to load course.", type: "error" });
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setForm(initialState);
      setPreviewImg(null);
      setBannerPreview(null);
    }
    // eslint-disable-next-line
  }, [courseId]);

  const set = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleFile = (file) => {
    if (!file) return;
    setForm((p) => ({ ...p, thumbnail: file }));
    setPreviewImg(URL.createObjectURL(file));
  };

  const handleBannerFile = (file) => {
    if (!file) return;
    setForm((p) => ({ ...p, banner: file }));
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      const thumbnailFile = payload.thumbnail;
      const bannerFile = payload.banner;
      delete payload.thumbnail;
      delete payload.banner;

      // Clean empty optional fields
      ["shortCode", "title", "duration", "enrollmentLink"].forEach(
        (k) => { if (!payload[k]) delete payload[k]; }
      );

      let savedCourseId = courseId;
      if (courseId) {
        await apiClient.put(`/courses/${courseId}`, payload);
      } else {
        const res = await apiClient.post("/courses/add", payload);
        savedCourseId = res.data?.course?._id || res.data?._id;
      }

      // Upload thumbnail via server-side upload if a new file was selected
      if (thumbnailFile && savedCourseId) {
        const formData = new FormData();
        formData.append("file", thumbnailFile);
        await apiClient.post(`/courses/${savedCourseId}/thumbnail`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Upload banner via server-side upload if a new file was selected
      if (bannerFile && savedCourseId) {
        const formData = new FormData();
        formData.append("file", bannerFile);
        await apiClient.post(`/courses/${savedCourseId}/banner`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setToast({ show: true, message: courseId ? "Course updated successfully!" : "Course added successfully!", type: "success" });
      setTimeout(() => navigate("/courses"), 1200);
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || "Failed to save course.",
        type: "error",
      });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sp-loading">
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  const isEdit = !!courseId;

  return (
    <div className="sp-form-page">
      {/* Top Bar */}
      <div className="sp-form-topbar">
        <div className="sp-form-topbar__left">
          <BackButton onClick={() => navigate("/courses")} />
          <div className="sp-form-topbar__info">
            <h5>{isEdit ? "Edit Course" : "Add New Course"}</h5>
            <span>Batch Course</span>
          </div>
        </div>
        <div className="sp-form-topbar__actions">
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => { setForm(initialState); setPreviewImg(null); }}
          >
            <RotateCcw size={14} /> Reset
          </AppButton>
          <AppButton type="submit" form="batchCourseForm" disabled={saving}>
            {saving ? (
              <><Spinner size="sm" animation="border" className="me-2" />Saving…</>
            ) : (
              <><Save size={15} /> {isEdit ? "Update Course" : "Save Course"}</>
            )}
          </AppButton>
        </div>
      </div>

      {/* Form Body */}
      <Form id="batchCourseForm" onSubmit={handleSubmit}>
        <Row className="g-4 mt-0">
          {/* Left Column */}
          <Col lg={7} xl={8}>
            {/* Basic Details */}
            <Section icon={<BookOpen size={18} />} title="Basic Details" subtitle="The essential info students see first">
              <Row className="g-3">
                <Col md={7}>
                  <Field label="Course Name" required>
                    <Form.Control
                      name="name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      required
                      placeholder="e.g., Full Stack Web Development"
                    />
                  </Field>
                </Col>
                <Col md={5}>
                  <Field label="Short Code" hint="Used as batch name prefix">
                    <Form.Control
                      className="sp-mono"
                      name="shortCode"
                      value={form.shortCode}
                      onChange={(e) => set("shortCode", e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                      placeholder="e.g., JAVA_FD"
                      maxLength={12}
                    />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Course Title / Tagline">
                    <Form.Control
                      name="title"
                      value={form.title}
                      onChange={(e) => set("title", e.target.value)}
                      placeholder="A short, catchy title shown under the name"
                    />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Description" hint="Describe the course outcomes and target audience">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="What will students learn? Who is this course for?"
                    />
                  </Field>
                </Col>
              </Row>
            </Section>

            {/* Pricing & Schedule */}
            <Section icon={<IndianRupee size={18} />} title="Pricing & Schedule" subtitle="Fees, demo availability, and duration">
              <Row className="g-3">
                <Col md={4}>
                  <Field label="Fees (INR)">
                    <div className="sp-input-icon-wrap">
                      <IndianRupee size={15} className="sp-input-icon" />
                      <Form.Control
                        type="number"
                        min={0}
                        className="sp-input-with-icon"
                        name="fees"
                        value={form.fees}
                        onChange={(e) => set("fees", e.target.value)}
                        placeholder="e.g., 14999"
                      />
                    </div>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Free Demo">
                    <Form.Select name="freeDemo" value={form.freeDemo} onChange={(e) => set("freeDemo", e.target.value)}>
                      <option>Yes</option>
                      <option>No</option>
                    </Form.Select>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Duration">
                    <div className="sp-input-icon-wrap">
                      <Clock size={15} className="sp-input-icon" />
                      <Form.Control
                        className="sp-input-with-icon"
                        name="duration"
                        value={form.duration}
                        onChange={(e) => set("duration", e.target.value)}
                        placeholder="e.g., 4 months"
                      />
                    </div>
                  </Field>
                </Col>
              </Row>
            </Section>

            {/* Batch Settings */}
            <Section icon={<Users size={18} />} title="Batch Settings" subtitle="Mode, capacity, and enrollment options">
              <Row className="g-3">
                <Col md={4}>
                  <Field label="Mode">
                    <Form.Select name="mode" value={form.mode} onChange={(e) => set("mode", e.target.value)}>
                      <option>Classroom</option>
                      <option>Online</option>
                      <option>Both</option>
                    </Form.Select>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Max Strength" hint="Max students per batch">
                    <Form.Control
                      type="number"
                      min={1}
                      name="maxStrength"
                      value={form.maxStrength}
                      onChange={(e) => set("maxStrength", e.target.value)}
                      placeholder="e.g., 40"
                    />
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Enrollment Link">
                    <div className="sp-input-icon-wrap">
                      <Link2 size={15} className="sp-input-icon" />
                      <Form.Control
                        className="sp-input-with-icon"
                        name="enrollmentLink"
                        value={form.enrollmentLink}
                        onChange={(e) => set("enrollmentLink", e.target.value)}
                        placeholder="Paste link (optional)"
                      />
                    </div>
                  </Field>
                </Col>
                <Col xs={12}>
                  <Form.Check
                    type="switch"
                    id="allowSelfEnrollment"
                    label="Allow Student Self-Enrollment"
                    checked={!!form.allowSelfEnrollment}
                    onChange={(e) => set("allowSelfEnrollment", e.target.checked)}
                  />
                </Col>
              </Row>
            </Section>

            {/* Video Security */}
            <VideoSecurityToggle form={form} set={set} />

            {/* Syllabus & Other Info */}
            <Section icon={<Layers size={18} />} title="Additional Details" subtitle="Enrich your course page — use headings, bullet points, and formatting" className="mb-4">
              <Row className="g-3">
                <Col xs={12}>
                  <Field label="Syllabus Outline">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.syllabus} onChange={(v) => set("syllabus", v)} modules={quillModules} formats={quillFormats} placeholder="Main syllabus points, modules, etc." />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Syllabus Outline", html: form.syllabus })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Prerequisites">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.prerequisites} onChange={(v) => set("prerequisites", v)} modules={quillModules} formats={quillFormats} placeholder="e.g., Basic programming knowledge" />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Prerequisites", html: form.prerequisites })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Key Highlights">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.highlights} onChange={(v) => set("highlights", v)} modules={quillModules} formats={quillFormats} placeholder="100% placement, Live projects, Certificate" />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Key Highlights", html: form.highlights })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
              </Row>
            </Section>
          </Col>

          {/* Right Column */}
          <Col lg={5} xl={4}>
            {/* Thumbnail dropzone */}
            <Section icon={<Image size={18} />} title="Course Thumbnail" subtitle="Recommended: 1280 × 720 px">
              <div
                className={`sp-dropzone ${dragActive ? "active" : ""} ${previewImg ? "has-image" : ""}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files[0]); }}
              >
                {previewImg ? (
                  <img src={previewImg} alt="thumbnail" className="sp-dropzone-preview" />
                ) : (
                  <div className="sp-dropzone-placeholder">
                    <Image size={32} strokeWidth={1.5} />
                    <span>Drop image here or click to browse</span>
                    <small>JPG, PNG, WebP up to 5 MB</small>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              {previewImg && (
                <button
                  type="button"
                  className="sp-remove-img"
                  onClick={(e) => { e.stopPropagation(); setPreviewImg(null); setForm((p) => ({ ...p, thumbnail: null })); }}
                >
                  Remove image
                </button>
              )}
            </Section>

            {/* Banner dropzone */}
            <Section icon={<Image size={18} />} title="Course Banner" subtitle="Recommended: 1920 × 480 px (wide banner for course detail page)">
              <div
                className={`sp-dropzone ${bannerDragActive ? "active" : ""} ${bannerPreview ? "has-image" : ""}`}
                onClick={() => bannerRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setBannerDragActive(true); }}
                onDragLeave={() => setBannerDragActive(false)}
                onDrop={(e) => { e.preventDefault(); setBannerDragActive(false); handleBannerFile(e.dataTransfer.files[0]); }}
              >
                {bannerPreview ? (
                  <img src={bannerPreview} alt="banner" className="sp-dropzone-preview" />
                ) : (
                  <div className="sp-dropzone-placeholder">
                    <Image size={32} strokeWidth={1.5} />
                    <span>Drop banner image here or click to browse</span>
                    <small>JPG, PNG, WebP up to 5 MB</small>
                  </div>
                )}
                <input
                  ref={bannerRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleBannerFile(e.target.files[0])}
                />
              </div>
              {bannerPreview && (
                <button
                  type="button"
                  className="sp-remove-img"
                  onClick={(e) => { e.stopPropagation(); setBannerPreview(null); setForm((p) => ({ ...p, banner: null })); }}
                >
                  Remove banner
                </button>
              )}
            </Section>

            {/* Quick Tips */}
            <div className="sp-tips-card">
              <div className="sp-tips-header"><Info size={15} /> Quick Tips</div>
              <ul className="sp-tips-list">
                <li>Use a compelling thumbnail — it's the first thing students see</li>
                <li>Set a short code to auto-prefix batch names</li>
                <li>Enable self-enrollment to let students apply directly</li>
                <li>Add batches after saving the course</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Form>

      {/* Rich-text Preview Modal */}
      <Modal
        show={previewModal.show}
        onHide={() => setPreviewModal({ show: false, title: "", html: "" })}
        centered
        size="lg"
        className="cr-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>{previewModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="cr-preview-body ql-editor" dangerouslySetInnerHTML={{ __html: previewModal.html || '<p style="color:var(--text-secondary,#999)">Nothing to preview yet.</p>' }} />
        </Modal.Body>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/* ── Video Security Toggle (reused in course forms) ───────── */
const VideoSecurityToggle = ({ form, set }) => {
  const { hasPlanFeature } = usePlan();
  const planAllows = hasPlanFeature('secureVideoPlayer') && hasPlanFeature('videoSecurityEnabled');

  return (
    <Section icon={<Shield size={18} />} title="Video Security" subtitle="Require the desktop app for students to watch videos in this course">
      <div
        className="sp-visibility-card"
        style={{ cursor: planAllows ? 'pointer' : 'default', opacity: planAllows ? 1 : 0.55 }}
        onClick={() => planAllows && set("videoSecurity", !form.videoSecurity)}
        role="button"
        tabIndex={0}
      >
        <div className={`sp-visibility-dot ${planAllows && form.videoSecurity ? 'live' : ''}`} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {planAllows && form.videoSecurity ? 'Enabled' : 'Disabled'}
          </div>
          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
            {!planAllows
              ? (!hasPlanFeature('secureVideoPlayer')
                  ? 'Your plan does not include video security. Upgrade to enable.'
                  : 'Video security is not enabled in institute settings. Enable it in Settings → Modules.')
              : form.videoSecurity
                ? 'Students must use the Desktop App to watch videos in this course.'
                : 'Students can watch videos in the browser — no desktop app required.'}
          </small>
        </div>
        <Form.Check
          type="switch"
          id="videoSecurity"
          checked={planAllows ? !!form.videoSecurity : false}
          onChange={() => planAllows && set("videoSecurity", !form.videoSecurity)}
          disabled={!planAllows}
          className="ms-2"
        />
      </div>
      <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
        Download prevention and watermarks are always active regardless of this setting.
      </small>
    </Section>
  );
};

export default AddOrEditCoursePage;
